import React from 'react'
import { BeWizrLibraryItem } from '~/models'
import { memo } from '~/ui/component'
import { Label, List, ListSection, VBox } from '~/ui/components'
import { createUseStyles, layout } from '~/ui/styling'
import renderLibraryItem from './items'
import i18next from 'i18next'
import { upperFirst } from 'lodash'
import { BeWizrLibraryViewListRenderProps } from './BeWizrLibraryView'

export type Props = BeWizrLibraryViewListRenderProps

const BeWizrCalendarView = memo('BeWizrCalendarView', (props: Props) => {

  const {data, ...listProps} = props

  const itemsByMonth = React.useMemo((): ListSection<BeWizrLibraryItem>[] => {
    const grouped = new Map<string, BeWizrLibraryItem[]>()
    for (const item of data) {
      const model = item.item
      if (!('startDate' in model) || model.startDate == null) { continue }
      const prettyDate  = upperFirst(model.startDate.toFormat('LLLL yyyy', {locale: i18next.language}))
      if (!grouped.has(prettyDate)) {
        grouped.set(prettyDate, [])
      }
      grouped.get(prettyDate)!.push(item)
    }
    return Array.from(grouped).map(([month, items]) => ({
      name:  month,
      items: items,
    }))
  }, [data])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <List
        {...listProps}
        data={itemsByMonth}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        itemGap={0}
      />
    )
  }

  function renderItem(item: BeWizrLibraryItem, index: number) {
    return (
      <VBox classNames={[$.item, {first: index === 0}]}>
        {renderLibraryItem(item)}
      </VBox>
    )
  }

  const renderSectionHeader = React.useCallback((section: ListSection<BeWizrLibraryItem>, index: number) => (
    <VBox classNames={$.header}>
      <Label bold small classNames={$.date}>{section.name}</Label>
    </VBox>
  ), [$.header, $.date])

  return render()

})

export default BeWizrCalendarView

const useStyles = createUseStyles(theme => ({
  header: {
    position: 'sticky',
    zIndex: layout.z.header,
    top: 0,
    borderBottom: [1, theme.border.dimmer, 'solid'],
    borderLeft: 0,
    borderRight: 0,
    background: theme.colors.bg.normal,
    ...layout.responsive(size => ({
      padding: [layout.padding.s[size], layout.padding.m[size]],
    })),
  },

  date: {
    textTransform: 'uppercase',
  },

  item: {
    ...layout.responsive(size => ({
      padding: [layout.padding.s[size] / 2, layout.padding.m[size]],
      '&.first': {
        paddingTop: layout.padding.m[size],
      },
    })),
  },
}))